import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import { forwardRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { retrieveCRMSettings, selectCRMSettings } from "../reducer"
import { isCallingPhoneReady } from "../utils"
import ConfirmDisconnectPhoneDialog from "./ConfirmDisconnectPhoneDialog"
import SettingsConnectionStatusRow from "./SettingsConnectionStatusRow"
import SetupRequiredStatusChip from "./SetupRequiredStatusChip"
import VerifyCallPhoneSection from "./VerifyCallPhoneSection"

const PhoneConnectionSettingsCard = forwardRef(
  function PhoneConnectionSettingsCard({ onStatusMessage }, ref) {
    const crmSettings = useSelector(selectCRMSettings)
    const phoneReady = isCallingPhoneReady(crmSettings)
    const settings = crmSettings.settings
    const dispatch = useDispatch()
    const axios = useAuthorizedAxiosClient()
    const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false)
    const [isDisconnecting, setIsDisconnecting] = useState(false)

    async function handleConfirmDisconnect() {
      setIsDisconnecting(true)
      try {
        await axios.post("/crm/api/twilio/disconnect-caller-phone/")
        await dispatch(retrieveCRMSettings())
        onStatusMessage?.("Phone number disconnected successfully", "success")
        setConfirmDisconnectOpen(false)
      } catch (err) {
        onStatusMessage?.(
          "Failed to disconnect phone number. Please try again.",
          "error"
        )
      } finally {
        setIsDisconnecting(false)
      }
    }

    return (
      <Card ref={ref} variant="outlined" sx={{ width: "100%" }}>
        <CardHeader
          title="Phone calling"
          titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
          subheader="Caller ID and verification for outbound calls to candidates"
          sx={{
            textAlign: "left",
            alignItems: "flex-start",
            pb: 1,
            "& .MuiCardHeader-content": { overflow: "visible" },
          }}
          subheaderTypographyProps={{
            variant: "body2",
            sx: { textAlign: "left", mt: 0.5 },
          }}
        />
        <CardContent
          sx={{
            pt: 0,
            px: 2,
            pb: 2,
            "&:last-child": { pb: 2 },
          }}
        >
          <Column sx={{ gap: 2, alignItems: "stretch" }}>
            <SettingsConnectionStatusRow>
              {phoneReady ? (
                <Row
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    minHeight: 32,
                    width: "100%",
                  }}
                >
                  <Chip
                    label="Connected"
                    size="small"
                    sx={(theme) => ({
                      backgroundColor: theme.palette.success.main,
                      color: theme.palette.success.contrastText,
                      fontWeight: 600,
                    })}
                  />
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ textAlign: "left" }}
                  >
                    ({settings.twilio_call_phone})
                    {settings.twilio_call_phone_verified ? (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={(theme) => ({
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          ml: 0.75,
                        })}
                      >
                        Verified
                      </Typography>
                    ) : null}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setConfirmDisconnectOpen(true)}
                    disabled={isDisconnecting}
                    sx={{ ml: 1 }}
                  >
                    Disconnect
                  </Button>
                </Row>
              ) : (
                <Row sx={{ alignItems: "center", minHeight: 32 }}>
                  <SetupRequiredStatusChip />
                </Row>
              )}
            </SettingsConnectionStatusRow>
            {!phoneReady && (
              <VerifyCallPhoneSection onStatusMessage={onStatusMessage} />
            )}
          </Column>
        </CardContent>
        <ConfirmDisconnectPhoneDialog
          open={confirmDisconnectOpen}
          onClose={() => setConfirmDisconnectOpen(false)}
          onConfirm={handleConfirmDisconnect}
          phone={settings?.twilio_call_phone}
          isLoading={isDisconnecting}
        />
      </Card>
    )
  }
)

export default PhoneConnectionSettingsCard
